import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  connectStripe,
  disconnectStripe,
  getStripeConnectStatus,
  updateStripeStatus,
} from "controller/api";
import type { ApiErrorType } from "shared";
import { sentryLogger } from "utilz";

export const useConnectStripe = () => {
  return useMutation({
    mutationFn: () => connectStripe(),
    onError: (error: ApiErrorType, variables) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "useConnectStripe",
      });
    },
  });
};

/**
 * Stripe Connect status for the current MSP.
 *
 * `enabled` is offered because this endpoint queries Stripe live rather than
 * reading a stored copy — it measures ~1.4-2.9s against ~0.9s for the
 * integration sources list — so callers that only need it conditionally should
 * say so rather than paying for it on every mount. Defaults to on, leaving
 * existing callers untouched.
 */
export const useGetStripeConnectStatus = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: ["stripeConnectStatus"],
    queryFn: () => getStripeConnectStatus(),
    enabled,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "useGetStripeConnectStatus",
      });
    },
  });
};

export const useDisconnectStripe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectStripe(),
    onSuccess: () => {
      queryClient.invalidateQueries(["stripeConnectStatus"]);
    },
    onError: (error: ApiErrorType, variables) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "useDisconnectStripe",
      });
    },
  });
};

export const useUpdateStripeStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: boolean) => updateStripeStatus(status),
    onSuccess: () => {
      queryClient.invalidateQueries(["stripeConnectStatus"]);
    },
    onError: (error: ApiErrorType, variables) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "useUpdateStripeStatus",
      });
    },
  });
};
